import React, { useState, useRef, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Minimal language picker for dark full-screen landing pages.
 * Renders a globe icon button; clicking it opens a compact dropdown.
 */
const LandingLanguagePicker: React.FC = () => {
  const { selectedLanguage, languages, setSelectedLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (languages.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-white p-2 hover:bg-white/10 rounded-full transition-colors"
        aria-label="Change language"
        aria-expanded={open}
      >
        <Globe className="w-5 h-5" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-xl shadow-xl py-1 z-50">
          {languages.map((lang) => {
            const active = selectedLanguage?.id === lang.id;
            return (
              <button
                key={lang.id}
                onClick={() => { setSelectedLanguage(lang); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${
                  active
                    ? 'text-white bg-white/10'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <span>{lang.native_name}</span>
                <span className="text-xs text-white/35 ml-2">{lang.code.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LandingLanguagePicker;
